import { makeStyles, Typography } from "@material-ui/core";
import { IconButton, CircularSlider } from '@coolremote/shared';
import { useTheme } from "@material-ui/core/styles";
import clsx from "clsx";
import _ from "lodash";
import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import tinycolor2 from "tinycolor2";
import { t } from "ttag";
import { useStoreActions, useStoreState } from "../../../../models/RootStore";
import {
  ArrowDownControl,
  ArrowUp,
  AutoMode,
  AutoVRMode,
  ColdMode,
  ControlWrapper,
  DryIcon,
  HotMode,
  ModeFanIcon,
  ScheduleOffIcon,
  SwitcherOffIcon,
  SwitcherOnIcon
} from "../../../../svgComponents";
import groupPanelStyles from "./GroupPanel.style";
import ModeText from "../../../../components/ModeText/ModeText";
import { getActiveSchedulesCount } from "../../../../utils/CommonUtils";
import { useGroupUniversalMembers } from "../../../../hooks/useGroupUniversalMembers";
import { getProtocolAdapter, ProtocolType } from "../../../../utils/universalUnit/protocols";
import { UNIVERSAL_PROPERTIES } from "../../../../utils/universalUnit/properties";
import { AC_SETPOINT_KEY, holdSetpoint, releaseHeldSetpoints } from "../../../../utils/heldSetpoints";

interface IObject {
  [key: string]: any;
}

const colorRanges: any = {
  COOL: ["#35a8e0", "#2d2e82"],
  HEAT: ["#f8b133", "#f05146"],
  AUTO: ["#35a8e0", "#f05146"],
  DRY: ["#266101", "#266101"],
  FAN: ["#4f00ed", "#4f00ed"],
  default: ["#ffffff", "#000000"]
};

const modeLimits: any = {
  1: [16, 32],
  2: [60, 90]
};

// Long enough to cover the 5s debounce before the request plus the device's reply
const SETPOINT_HOLD_MS = 20000;

const wsMessageNames: Record<ProtocolType, string> = {
  bacnet: "UPDATE_BACNET_UNIT_DATA",
  modbus: "UPDATE_MODBUS_UNIT_DATA"
};

interface IUniversalSetpointTarget {
  id: string;
  protocol: ProtocolType;
  pointKey: string;
  value?: any;
}

interface IPreviousSetpoints {
  units: Record<string, any>;
  universal: IUniversalSetpointTarget[];
}

const fDegree = 360 / 118;
const cDegree = 360 / 48;

const GroupPanel: React.FC<any> = (props) => {
  const useStyles = makeStyles(groupPanelStyles);
  const classes = useStyles();
  const theme = useTheme();

  const modeIcons: IObject = {
    COOL: <ColdMode className={clsx(classes.iconStyle, classes.modeIcon)} />,
    HEAT: <HotMode className={clsx(classes.iconStyle, classes.modeIcon)} />,
    AUTO: (
      <AutoMode
        className={clsx(
          classes.iconStyle,
          classes.modeIcon,
          classes.smallIcons
        )}
      />
    ),
    "Virtual Auto": (
      <AutoVRMode
        className={clsx(
          classes.iconStyle,
          classes.modeIcon,
          classes.smallIcons
        )}
      />
    ),
    DRY: (
      <DryIcon
        className={clsx(
          classes.iconStyle,
          classes.modeIcon,
          classes.smallIcons
        )}
      />
    ),
    FAN: (
      <ModeFanIcon
        className={clsx(
          classes.iconStyle,
          classes.modeIcon,
          classes.smallIcons
        )}
      />
    )
  };

  const changeGroupPower = useStoreActions((state) => state.groups.changePowerState);
  const changeGroupSetPoint = useStoreActions((state) => state.groups.changeSetPoint);
  const allUnits = useStoreState((state) => state.units.allUnits);
  const devices = useStoreState((state) => state.devices.allDevices);
  const allSensors = useStoreState((state) => state.sensors.allSensors);
  const updateMultiUnitsLocally = useStoreActions((actions) => actions.units.updateMultiUnitsLocally);
  const broadcastWebSocketMessage = useStoreActions((actions: any) => actions.broadcastWebSocketMessage);

  const {
    group,
    navigateToSchedules,
    selectedSiteId,
    types,
    temperatureScale: userTempScale,
    addMessage,
    operationStatusesMirror,
    isCelsius,
    siteTemp,
    tempSymbol,
    changeSitePower,
    siteId,
    allSchedules,
    hideSchedules = false,
    canControlSiteOperationStatus = true
  } = props;
  const {
    id: groupId = "",
    units: groupUnits = [],
    name: groupName = "",
    schedules = [],
    sensors: groupSensors,
    permissions = {}
  } = group || {};

  const { canControlSetpoint, canControlOperationStatus } = permissions;

  const { members: universalMembers } = useGroupUniversalMembers({ group, siteId });
  // The members' setpoints from before the current change, to roll back to if the request fails
  const previousSetpointsRef = useRef<IPreviousSetpoints | null>(null);

  const [currentColor, setCurrentColor] = useState<any>("#888");
  const [tempLimits, setTempLimits] = useState<any>(modeLimits[userTempScale]);
  const [angles, setAngles] = useState<any>([0, 360]);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [mode, setMode] = useState<any>(null);
  const [setpoint, setSetpoint] = useState<any>(null);
  const [power, setPower] = useState<boolean>(false);
  const [keepUserChange, handleKeepingUserChange] = useState<boolean>(false);
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  const [resetGroupData, handleResetGroupData] = useState<boolean>(false);
  const [enableSetpointHandlers, setEnableSetpointHandlers] = useState<boolean>(false);

  const activeSchedulesCount = getActiveSchedulesCount(schedules, allSchedules);
  const hasSchedules = activeSchedulesCount > 0;
  const { operationModesExtended } = types;
  const powerOnVal = operationStatusesMirror && +operationStatusesMirror.on;

  useEffect(() => {
    if (keepUserChange) {
      return;
    }

    let power: boolean = false;
    let setPoint: any = null;
    let mode: any = null;
    let first: boolean = true;
    let oneAtleastIsConnected: boolean = false;
    let allUnitsHasSetpointControl: boolean = false;
    let checkForNum: boolean = false;
    let checkForNumMode: boolean = false;

    for (let x in groupUnits) {
      const id = groupUnits[x];
      const unit = allUnits[id] || {};
      if (!unit || unit?.subType === 1 || !unit?.isVisible || !unit?.isConnected || !devices[unit?.device || ""]?.isConnected) {
        continue;
      }
      const { capabilityFlags = {} } = unit;
      const { enableOperationModeSelection = false, enableSetpointControl = false } = capabilityFlags;

      oneAtleastIsConnected = true;
      if (first) {
        power = unit.activeOperationStatus === powerOnVal;
        setPoint = enableSetpointControl ? unit.activeSetpoint : null;
        mode = enableOperationModeSelection ? unit.activeOperationMode : null;
        first = false;
        allUnitsHasSetpointControl = enableSetpointControl;
        if (!checkForNum && enableSetpointControl) {
          checkForNum = true;
        }
        if (!checkForNumMode && enableOperationModeSelection) {
          checkForNumMode = true;
        }
        continue;
      }
      if (!checkForNum && enableSetpointControl) {
        checkForNum = true;
        setPoint = unit.activeSetpoint;
      }
      if (!checkForNumMode && enableOperationModeSelection) {
        checkForNumMode = true;
        mode = unit.activeOperationMode;
      }
      if (!power && unit.activeOperationStatus === powerOnVal) {
        power = true;
      }
      if (enableSetpointControl && setPoint !== null && setPoint !== unit.activeSetpoint) {
        setPoint = null;
      }
      if (enableOperationModeSelection && mode !== null && mode !== unit.activeOperationMode) {
        mode = null;
      }
      if (allUnitsHasSetpointControl) {
        continue;
      }
      allUnitsHasSetpointControl = enableSetpointControl;
    }
    if (!oneAtleastIsConnected || !power) {
      for (let x in groupSensors) {
        const id = groupSensors[x];
        const sensor: any = allSensors[id];
        if (!sensor || !devices[sensor?.device || ""]?.isConnected) {
          continue;
        }
        if (sensor?.type === 129) {
          oneAtleastIsConnected = true;
          if (sensor?.readingValue === 1) {
            power = true;
          }
        }
      }
    }

    const modeText = mode === null ? "default" : operationModesExtended[mode];
    const limits = modeLimits[userTempScale];
    const defaultSetpoint = setPoint === null ? isCelsius ? 24 : 75 : setPoint;
    const minLimit = Math.min(limits[0], defaultSetpoint);
    const maxLimit = Math.max(limits[1], defaultSetpoint);
    const degree = isCelsius ? cDegree : fDegree;

    setTempLimits([minLimit, maxLimit]);
    setAngles([degree * minLimit, degree * maxLimit]);
    setSetpoint(setPoint === null ? "--" : Math.round(setPoint));
    setMode(modeText);
    setPower(power);
    setDisabled(!oneAtleastIsConnected);
    isFirstLoad && setIsFirstLoad(false);
    setEnableSetpointHandlers(allUnitsHasSetpointControl);
  }, [groupId, allUnits, resetGroupData, userTempScale, allSensors]);

  // The AC units the group dial is built from, limited to those whose setpoint the change reaches
  const getSetpointUnitIds = (): string[] =>
    groupUnits.filter((id: string) => {
      const unit = allUnits[id];
      if (!unit || unit.subType === 1 || !unit.isVisible || !unit.isConnected) {
        return false;
      }
      return !!devices[unit.device || ""]?.isConnected && !!unit.capabilityFlags?.enableSetpointControl;
    });

  // The BACnet/Modbus members with a mapped setpoint point, and the key it is pushed under
  const getUniversalSetpointTargets = (): IUniversalSetpointTarget[] =>
    universalMembers.reduce((targets: IUniversalSetpointTarget[], { id, protocol, entry, mappedPoints }) => {
      const point = mappedPoints[UNIVERSAL_PROPERTIES.SETPOINT];
      if (!point) {
        return targets;
      }
      const adapter = getProtocolAdapter(protocol);
      const pointKey = protocol === "bacnet" ? adapter.buildPointKey(point.objectId) : adapter.buildPointKey(point.row || point);
      if (pointKey) {
        targets.push({ id, protocol, pointKey, value: entry?.operationalData?.[pointKey] });
      }
      return targets;
    }, []);

  const pushUniversalSetpoint = ({ id, protocol, pointKey }: IUniversalSetpointTarget, value: any) => {
    broadcastWebSocketMessage({
      name: wsMessageNames[protocol],
      data: { unitId: id, operationalData: { [pointKey]: value }, partial: true }
    });
  };

  // Shows the new setpoint on every member right away, instead of after the debounce and the devices' reply
  const showSetpointOnMembers = (value: number) => {
    const unitIds = getSetpointUnitIds();
    const universalTargets = getUniversalSetpointTargets();

    if (!previousSetpointsRef.current) {
      const units: Record<string, any> = {};
      unitIds.forEach((id) => {
        units[id] = allUnits[id].activeSetpoint;
      });
      previousSetpointsRef.current = { units, universal: universalTargets };
    }

    const updates: Record<string, any> = {};
    unitIds.forEach((id) => {
      updates[id] = { activeSetpoint: value };
      holdSetpoint(id, AC_SETPOINT_KEY, value, SETPOINT_HOLD_MS);
    });
    updateMultiUnitsLocally(updates);

    universalTargets.forEach((target) => {
      holdSetpoint(target.id, target.pointKey, value, SETPOINT_HOLD_MS);
      pushUniversalSetpoint(target, value);
    });
  };

  const restoreMemberSetpoints = () => {
    const previous = previousSetpointsRef.current;
    previousSetpointsRef.current = null;
    if (!previous) {
      return;
    }
    const unitIds = Object.keys(previous.units);
    releaseHeldSetpoints([...unitIds, ...previous.universal.map(({ id }) => id)]);

    const updates: Record<string, any> = {};
    unitIds.forEach((id) => {
      updates[id] = { activeSetpoint: previous.units[id] };
    });
    updateMultiUnitsLocally(updates);

    previous.universal.forEach((target) => {
      if (target.value === undefined) {
        return;
      }
      pushUniversalSetpoint(target, target.value);
    });
  };

  const delayedCall = useCallback(_.debounce((newValue: any) => {
    changeGroupSetPoint({ groupId, setPoint: newValue })
      .then(() => {
        previousSetpointsRef.current = null;
        setTimeout(() => handleKeepingUserChange(false), 2000);
      })
      .catch((err: any) => {
        addMessage({ message: err.message });
        restoreMemberSetpoints();
        handleKeepingUserChange(false);
        handleResetGroupData(!resetGroupData);
      });
  }, 5000), [resetGroupData]);

  const changeTemp = (isAdd: boolean) => {
    const newValue = setpoint === "--" ? (isCelsius ? 24 : 75) : (isAdd ? setpoint + 1 : setpoint - 1);
    !keepUserChange && handleKeepingUserChange(true);
    setSetpoint(newValue);
    showSetpointOnMembers(newValue);
    delayedCall(newValue);
  };
  const handleTemp = (value: number) => {
    !keepUserChange && handleKeepingUserChange(true);
    setSetpoint(value);
    showSetpointOnMembers(value);
    delayedCall(value);
  };

  useEffect(() => {
    if (setpoint === null || mode === null) {
      return;
    }
    const range = colorRanges[mode] ? colorRanges[mode] : colorRanges["default"];
    const c1 = range[0];
    const c2 = range[1];
    const absoluteVal = ((setpoint - tempLimits[0]) / (tempLimits[1] - tempLimits[0])) * 100;
    const c3 = tinycolor2.mix(c1, c2, absoluteVal);
    setCurrentColor(c3.toHexString());
  }, [setpoint, mode]);

  const togglePower = () => {
    setPower(!power);
    const state = power ? 2 : 1; //if power is on sends off value and vice versa
    groupId === "allUnits" ? changeSitePower({ state, siteId }) :
      changeGroupPower({ groupId, state })
        .catch((error: any) => addMessage({ message: error.message }));
  };

  const noControl = mode === "DRY" || mode === "FAN" || groupId === "allUnits" || !enableSetpointHandlers;
  if (groupId === "sensors" || _.isEmpty(groupUnits)) {
    return (<> </>);
  }

  return (
    <div className={classes.groupView}>
      <div className={classes.firstRowContainer}>
        <Typography className={classes.siteTemp}>{(isCelsius ? Math.round(siteTemp.C) || "" : Math.round(siteTemp.F) || "") + " "}<span className={classes.scaleStyle}>{siteTemp.C && tempSymbol || ""}</span></Typography>
        <div className={classes.nameSection}>
          <Typography>{groupName}</Typography>
          <div className={classes.loaderContainer}>
          </div>
        </div>

        <div className={clsx(classes.powerContainer, { [classes.disabled]: disabled })}>
          <IconButton
            id={"groupPowerButton"}
            className={classes.powerIconStyle}
            onClick={() => togglePower()}
            disabled={disabled || (groupId === "allUnits" ? !canControlSiteOperationStatus : !canControlOperationStatus)}
          >
            {power ?
              <SwitcherOnIcon className={classes.powerOffButtonClick} /> :
              <SwitcherOffIcon className={classes.powerOnButtonClick} />
            }
          </IconButton>
          <Typography className={classes.powerTextStyle}>
            {power ? t`POWER OFF` : t`POWER ON`}
          </Typography>
        </div>
      </div>

      <div className={classes.secondRowContainer}>
        <div className={classes.controlDiv} >
          <ControlWrapper
            className={clsx(classes.controlWrapperStyle)}
            fill={power ? currentColor : "rgba(253,253,254, 0.5)"}
            width={336}
            height={326}
          />
          {!noControl ? (
            <Fragment>
              <div
                className={clsx(classes.sliderContainer)}
              >
                {<CircularSlider
                  size={320}
                  minValue={tempLimits[0]}
                  maxValue={tempLimits[1]}
                  startAngle={angles[0]}
                  endAngle={angles[1]}
                  coerceToInt={true}
                  angleType={{
                    direction: "cw",
                    axis: "-y"
                  }}
                  handle1={{
                    value:
                      setpoint === "--" ? tempLimits[0] : setpoint,
                    onChange: (value: any) => canControlSetpoint && handleTemp(value)
                  }}
                  arcColor={theme.palette.primary.light}
                  arcBackgroundColor={theme.palette.primary.light}
                  disabled={disabled || groupId === "allUnits"}
                />}
              </div>
              <div
                className={clsx(classes.innerControls)}
              >
                {(canControlSetpoint && groupId !== "allUnits") && <IconButton
                  onClick={() => changeTemp(true)}
                  className={clsx(classes.controlArrow, { [classes.disabled]: disabled || (tempLimits[1] - 1 < setpoint) })}
                  disabled={disabled || (tempLimits[1] - 1 < setpoint)}
                >
                  <ArrowUp />
                </IconButton>}

                <div className={classes.localSetpoint}>
                  <Typography className={clsx(classes.setPointStyle)}>  {setpoint === "--" || setpoint === null ? "--" : Math.round(setpoint)}</Typography>
                  <span>{isCelsius ? t`°C` : t`°F`}</span>
                </div>
                <div className={clsx(classes.ampTempContainer, classes.textColor)}>
                  {modeIcons[mode] || (
                    <ModeText text={mode !== "default" ? mode : ""} />
                  )}
                </div>
                {(canControlSetpoint && groupId !== "allUnits") && <IconButton
                  onClick={() => changeTemp(false)}
                  className={clsx(classes.controlArrow, { [classes.disabled]: disabled || (tempLimits[0] + 1 > setpoint) })}
                  disabled={disabled || (tempLimits[0] + 1 > setpoint)}
                >
                  <ArrowDownControl />
                </IconButton>}
              </div>
            </Fragment>
          ) : (
            <Typography className={classes.modeTitle}>{mode === "default" ? "--" : mode}</Typography>
          )}
        </div>
      </div>

      {groupId !== "allUnits" && <div className={classes.lastRowContainer}>
        <div className={classes.iconContainer}>
          {!hideSchedules &&
            <IconButton
              disableFocusRipple
              disableRipple
              className={classes.mainIconsStyle}
              name="schedule"
              onClick={() => navigateToSchedules(selectedSiteId, groupId)}
            >
              <ScheduleOffIcon />
              {hasSchedules && <span className={classes.badge}>{activeSchedulesCount}</span>}
            </IconButton>}
        </div>
      </div>}
    </div>
  );
};

export default GroupPanel;
